import { readonly, ref, watch, type ComputedRef, type Ref, type WatchStopHandle } from 'vue'

import {
  clearShellEventSubscribers,
  dispatchShellEvent,
  getShellEventSubscriberCount,
  onShellEvent,
  onShellEventSubscriberCountChange,
  sealShellEventNames,
  type ShellEventHandler,
  type ShellEventMap,
  type ShellEventName,
} from './shellEventRegistry'

import { useAccountSession, type SessionStatus } from '@/features/account'
import { openEventStream, type EventStreamHandle, type EventStreamOptions } from '@/shared/http'

const STREAM_PATH = '/events/stream/'

export interface ShellEventBus {
  readonly connected: Readonly<Ref<boolean>>
  on: <Name extends ShellEventName>(name: Name, handler: ShellEventHandler<Name>) => () => void
}

export interface ShellEventRuntimeDeps {
  status: ComputedRef<SessionStatus>
  workspaceId: ComputedRef<number | string | undefined>
  /** Optional transport lease. Production binds this to the live feature-subscriber count. */
  active?: Readonly<Ref<boolean>>
  openStream?: (options: EventStreamOptions) => EventStreamHandle
}

export interface ShellEventRuntime {
  /** Close the active handle without stopping the app-lifetime workspace/session watcher. */
  resetConnection: () => void
  /** Full teardown for app unmount/test isolation. */
  stop: WatchStopHandle
}

const connected = ref(false)

export const shellEventBus: ShellEventBus = {
  connected: readonly(connected),
  on: onShellEvent,
}

/**
 * Own one subscriber-leased, workspace-scoped EventSource. The generation fence is independent
 * of EventSource.close: even a queued frame from the old source is dropped after the last
 * unsubscribe, a workspace switch, or logout.
 */
export function createShellEventRuntime(deps: ShellEventRuntimeDeps): ShellEventRuntime {
  const openStream = deps.openStream ?? openEventStream
  const eventNames = sealShellEventNames()
  let handle: EventStreamHandle | null = null
  let activeWorkspace: string | null = null
  let generation = 0

  const closeActive = (): void => {
    generation += 1
    activeWorkspace = null
    connected.value = false
    handle?.close()
    handle = null
  }

  const sync = (): void => {
    const workspaceId = deps.workspaceId.value
    if (
      deps.active?.value === false ||
      deps.status.value !== 'ready' ||
      workspaceId === undefined
    ) {
      if (handle) closeActive()
      return
    }

    const workspaceKey = String(workspaceId)
    if (handle && activeWorkspace === workspaceKey) return
    if (handle) closeActive()

    const streamGeneration = ++generation
    activeWorkspace = workspaceKey
    const isCurrent = (): boolean =>
      generation === streamGeneration && activeWorkspace === workspaceKey

    const events = Object.fromEntries(
      eventNames.map((name) => [
        name,
        (payload: unknown): void => {
          if (!isCurrent()) return
          dispatchShellEvent(name, payload as ShellEventMap[typeof name])
        },
      ]),
    ) as EventStreamOptions['events']

    handle = openStream({
      path: STREAM_PATH,
      query: { workspace_id: workspaceId },
      events,
      onOpen: () => {
        if (isCurrent()) connected.value = true
      },
      onError: () => {
        if (isCurrent()) connected.value = false
      },
    })
  }

  const stopWatch = watch(
    deps.active ? [deps.status, deps.workspaceId, deps.active] : [deps.status, deps.workspaceId],
    sync,
    {
      immediate: true,
      flush: 'sync',
    },
  )

  return {
    resetConnection: closeActive,
    stop: () => {
      stopWatch()
      closeActive()
    },
  }
}

let installedRuntime: ShellEventRuntime | null = null

/** Production install point. Repeated calls are a no-op; live unique subscribers lease one stream. */
export function installShellEventBus(deps?: Omit<ShellEventRuntimeDeps, 'active'>): ShellEventBus {
  if (installedRuntime) return shellEventBus
  const session = deps ? null : useAccountSession()
  const subscriberActive = ref(getShellEventSubscriberCount() > 0)
  const runtime = createShellEventRuntime({
    ...(deps ?? {
      status: session!.status,
      workspaceId: session!.workspaceId,
    }),
    active: subscriberActive,
  })
  const stopSubscriberWatch = onShellEventSubscriberCountChange((count) => {
    subscriberActive.value = count > 0
  })
  installedRuntime = {
    resetConnection: runtime.resetConnection,
    stop: () => {
      stopSubscriberWatch()
      runtime.stop()
    },
  }
  return shellEventBus
}

/** Logout/session reset: close the handle and erase every session-owned subscription. */
export function resetShellEventBus(): void {
  installedRuntime?.resetConnection()
  clearShellEventSubscribers()
  connected.value = false
}

/** App teardown/test isolation; bootstrap's production lifetime never calls this. */
export function disposeShellEventBus(): void {
  installedRuntime?.stop()
  installedRuntime = null
  clearShellEventSubscribers()
  connected.value = false
}
